import type { AgreementData } from '@/shared/elements/common/payment/types';
import { type PageResponse, PageType } from '@/types';
import { getFirstPage } from '@/utils/session';
import type { RegisterType } from '@shared/types/model'

type CurrentPage = {
  id: number;
  appId: number;
  flowId: number;
  pollId: number | null;
  title: string;
  order: number;
  status: 1 | 0;
  type: PageType;
  page: string;
  locales: Record<string, any>;
  template: {
    id: number;
    name: PageType;
    title: string;
    batch: null;
    defaultOrder: number;
    status: 1 | 0;
    isMandatory: 1 | 0;
    isStatusChangeable: 1 | 0;
    createDate: string;
    updateDate: string;
  };
};

export type SiteData = {
  maintenance: boolean;
  noparams?: boolean;
  path: string;
  pageSlug: string;
  pageOptions: string | null;
  redirectTo?: string;
  bypassRegister?: boolean;
  registerType?: RegisterType;
  registerOnPayment?: boolean;
  allowSubscriberIdEditingOnRegisterPayment?: boolean;
  registerOtpStatus?: boolean;
  countryCode: string;
  phoneNumberAllowedCountries?: string[];
  sessionId: string;
  expireAt: string;
  config: Record<string, any>;
  hideGenerateFooter: boolean;
  integrations: {
    gtmData: {
      isActive: 0 | 1;
      gtmCode: string;
      gtmDomain?: string;
    };
    googleAds: {
      isActive: 0 | 1;
      gTag: string;
      conversionId: string;
      conversionLabel: string
    };
    gaData: {
      isActive: 0 | 1;
      gaCode: string;
    };
    facebookData: {
      isActive: 0 | 1;
      pixelId: string;
      integrationType: 'pixel' | 'capi' | 'both';
    }
  };
  availablePages: PageType[];
  page: CurrentPage;
  lang: string;
  preview: PageResponse['result']['preview'];
  sandbox: PageResponse['result']['sandbox'];
  isStoreFlow: PageResponse['result']['isStoreFlow'];
  storeHash: PageResponse['result']['storeHash'];
  languageData: PageResponse['result']['languageData'];
  applicationLinks: PageResponse['result']['applicationLinks'];
  deeplinkSettings: PageResponse['result']['deeplinkSettings'];
  social: PageResponse['result']['social'];
  providers: {
    stripe: boolean;
    creditCard: boolean;
    paypal: boolean;
    applePay: boolean;
    googlePay: boolean;
  } | undefined;
  isUserAlreadySubscribed: boolean;
  subscriberId: string;
  isPolicyRequired?: boolean;
  isZipcodeRequired?: boolean;
  flowImages: string[];
  epinDetails: PageResponse['result']['epinDetails'];
  websiteRedirectionSettings: PageResponse['result']['websiteRedirectionSettings'];
  userActivationData: PageResponse['result']['userActivationData'];
  localeParams: Record<string, any>;
  restrictedCountries: PageResponse['result']['restrictedCountries'];
};

export function useSiteData() {
  const { data } = useNuxtApp().payload;
  const siteData = computed<SiteData>(() => data?.['INITAPP'] || {});
  const packages = computed<Record<string, any>[]>(() => data?.['PACKAGES']?.packages || []);
  const agreement = computed<AgreementData>(() => data?.['PACKAGES']?.agreement);
  const language = computed(() => siteData.value?.lang);
  const currentPage = computed(() => siteData.value?.page);
  const integrations = computed(() => siteData.value?.integrations || {});
  const providers = computed<SiteData['providers']>(() => siteData.value?.providers || {} as SiteData['providers']);
  const applicationLinks = computed(() => siteData.value?.applicationLinks || {});
  const socialLoginStatus = computed(() => siteData.value?.social || {});
  const availablePages = computed(() => siteData.value?.availablePages || []);
  const config = computed(() => siteData.value?.config || {});
  const isPreview = computed(() => !!siteData.value?.preview);
  const isSandbox = computed(() => !!siteData.value?.sandbox);
  const isStoreFlow = computed(() => !!siteData.value?.isStoreFlow);
  const nextPage = computed(() => getNextPage());
  const countryCode = computed(() => siteData.value?.countryCode);
  const epinDetails = computed(() => siteData.value?.epinDetails || {});
  const isMaintenance = computed(() => siteData.value?.maintenance);
  const parametersNotProvided = computed(() => siteData.value?.noparams);
  const languages = computed(() => {
    const languagesObj = siteData.value?.localeParams?.languages || {};
    return Object.entries(languagesObj).map(([key, value]: any[]) => {
      return {
        label: key,
        value: value?.code,
        ...value
      };
    });
  });
  const countries = computed(() => {
    const countriesObj = siteData.value?.localeParams?.countries || {};
    return Object.entries(countriesObj).map(([key, value]: any[]) => {
      return {
        label: value?.name,
        value: key,
        ...value
      };
    });
  });
  const restrictedCountries = computed(() => siteData.value?.restrictedCountries || []);

  function getNextPage() {
    const pageType = currentPage.value?.template?.name || getFirstPage();
    const currentPageIndex = availablePages.value?.indexOf(pageType);
    const availablePagesCount = availablePages.value?.length;
    const nextPageIndex = (currentPageIndex + 1) % availablePagesCount;
    let newPage = availablePages.value[nextPageIndex];

    if (newPage === PageType.REGISTER && siteData.value?.bypassRegister) {
      newPage = availablePages.value[(currentPageIndex + 2) % availablePagesCount];
    }

    return newPage;
  }

  return {
    packages,
    agreement,
    siteData,
    currentPage,
    availablePages,
    integrations,
    providers,
    config,
    language,
    isPreview,
    isSandbox,
    isStoreFlow,
    nextPage,
    applicationLinks,
    socialLoginStatus,
    countryCode,
    epinDetails,
    languages,
    countries,
    restrictedCountries,
    isMaintenance,
    parametersNotProvided,
    getNextPage
  }
}
